import {
  AdminReviewStatus,
  DocumentStatus,
  BusinessDocument,
} from "@/types/business";

export interface StatusMeta {
  label: string;
  variant: "pending" | "processing" | "success" | "rejected" | "default";
  description: string;
}

export type StatusVariant = StatusMeta["variant"];

export const STATUS_VARIANT_CLASSES: Record<StatusVariant, string> = {
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
  processing: "bg-blue-50 text-blue-700 border-blue-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  default: "bg-slate-50 text-slate-600 border-slate-200",
};

export function getDocumentStatusMeta(doc?: BusinessDocument | null): StatusMeta {
  if (!doc) {
    return {
      label: "Not Uploaded",
      variant: "default",
      description: "Document has not been submitted yet.",
    };
  }

  // 1. Check if Admin has rejected it first
  if (doc.admin_review_status === AdminReviewStatus.REJECTED) {
    return {
      label: "Re-upload Required",
      variant: "rejected",
      description: doc.rejection_reason || "Admin rejected this document. Please re-upload.",
    };
  }

  // 2. Check if final verification status is rejected
  if (doc.status === DocumentStatus.REJECTED) {
    return {
      label: "Rejected",
      variant: "rejected",
      description: doc.rejection_reason || "Verification failed. Please update your document.",
    };
  }

  // 3. Final Verification Complete
  if (doc.status === DocumentStatus.VERIFIED) {
    return {
      label: "Document Verified",
      variant: "success",
      description: "Your document has been verified successfully.",
    };
  }

  // 4. Admin approved, waiting for final system verification status update
  if (doc.admin_review_status === AdminReviewStatus.APPROVED) {
    return {
      label: "Processing",
      variant: "processing",
      description: "Admin approved. Final automated check in progress.",
    };
  }

  // 5. Default initial state after upload (Pending Admin Review)
  return {
    label: "Pending Review",
    variant: "pending",
    description: "Your document is currently under admin review.",
  };
}

export function getKycStatusMeta(status?: string | null): StatusMeta {
  const normalized = (status ?? "").trim().toLowerCase();

  if (normalized === "verified" || normalized === "approved") {
    return {
      label: "Verified",
      variant: "success",
      description: "Your account is verified.",
    };
  }

  if (normalized.includes("reject")) {
    return {
      label: "Rejected",
      variant: "rejected",
      description: "Action required, review your documents.",
    };
  }

  if (normalized && normalized !== "unverified" && normalized !== "none") {
    return {
      label: "Pending",
      variant: "pending",
      description: "Verification in progress.",
    };
  }

  return {
    label: "Get Verified",
    variant: "default",
    description: "Get verified to unlock full platform features.",
  };
}